import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Button, Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { RootState } from '../../../redux/store';
import { setSelectedView } from '../../../redux/userProfileSlice';
import { menuRoutes } from "../../../router/menu";
import { UserService } from '../../../services/user/userService';
import { UserApiResponse } from '../../../type/entities/user-type';
import PasswordChangeModal from "./views/info/PasswordChangeModal";
import CustomSpinner from '../../../loader/CustomSpinner';
import {useDispatch} from "react-redux";

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: 'info',
        permission: {
            group: 'user',
            action: 'get',
        },
    },
    {
        label: 'Permisos',
        path: 'permissions',
        permission: {
            group: 'user',
            action: 'get_permissions',
        },
    }
];

export default function UserProfileLayout() {

    // STATES

    const [loginIsLoading, setIsLoading] = useState<boolean>(false);
    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);

    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.userProfile);
    const { user } = useSelector((state: RootState) => state.auth);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const service = new UserService();
    const dispatch = useDispatch();

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER LOS DATOS DEL USUARIO
     * @EN GET USER DATA
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getUserById(id);
        return response.getResponseData() as UserApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    const pageHeader = {
        title: `Perfil de ${data?.name || 'Usuario'} ${data?.lastName || ''}`,
        breadcrumb: [
            { name: 'Administración' },
            { name: 'Control de acceso' },
            { name: 'Usuarios', href: menuRoutes.users.path },
            { name: 'Perfil de Usuario' },
        ],
    };

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL CAMBIO DE ESTADO DEL USUARIO
     * @EN HANDLE THE CHANGE OF USER STATUS
     */
    //--------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleUserStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el usuario" : "Se ha activado el usuario");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL BORRADO DEL USUARIO
     * @EN HANDLE THE DELETION OF THE USER
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteUser(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.users.path);
                toast.success('Se ha eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        dispatch(setSelectedView('info'));
    }, [id]);
    useEffect(() => {
        navigate(`${menuRoutes.users.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {userCan('edit', 'user') && (
                        <>


                            {data && (
                                <>
                                    <Tooltip
                                        size="sm"
                                        content={data.active ? 'Desactivar usuario' : 'Activar usuario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={id}
                                            label={data.active ? 'Activo' : 'Desactivado'}
                                            checked={data.active}
                                            disabled={changingStatus.includes(data.id) || data.id === user?.id}
                                            onChange={() => toggleStatus(data.id, data.active)}
                                            switchKnobClassName='bg-secondary'
                                        />
                                    </Tooltip>
                                    <div className='border-l border-gray-300' />

                                </>


                            )}

                            {userCan('edit_permissions', 'user') && (
                                <Tooltip content={'Editar Permisos del usuario'} placement={'top'} color={'invert'} size={'sm'}>
                                    <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                        navigate(`${menuRoutes.users.path}/${id}/edit/permissions`)
                                    }}>
                                        Editar Permisos
                                    </Button>
                                </Tooltip>
                            )}

                            {userCan('edit', 'user') && user?.id === id && (
                                <Tooltip content={'Cambiar Contraseña'} placement={'top'} color={'invert'} size={'sm'}>
                                    <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => setPasswordModalOpen(true)}>
                                        Cambiar Contraseña
                                    </Button>
                                </Tooltip>
                            )}
                        </>
                    )}

                    <PasswordChangeModal userId={id} open={isPasswordModalOpen} onClose={() => setPasswordModalOpen(false)} />

                    {userCan('delete', 'user') && (data && data.id !== user?.id) && (
                        <DeletePopover
                            title={`Eliminar usuario`}
                            description={`¿Estás seguro de que deseas eliminar el usuario?`}
                            size={'md'}
                            onDelete={() => handleDelete(id)}
                            actionIconClassName='h-auto w-10'
                        />
                    )}
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {loading ? <CustomSpinner /> : <Outlet />}
        </>
    );
}